import { Suspense, useMemo, useState } from 'react';

import { type QueryOptions } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Button } from '@mui/material';
import type { GridRowId } from '@mui/x-data-grid-premium';
import { Columns3, LayoutList } from 'lucide-react';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import {
  type GetLeadsQuery,
  type GetLeadsQueryVariables,
  type Leads_Bool_Exp,
  type Leads_Order_By,
} from '@graphql/__generated__/graphql';
import { CreateLeadDrawer } from '@modules/_shared/components/create-lead/CreateLeadDrawer';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { OptionalTooltip } from '@modules/_shared/components/OptionalTooltip';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { useBulkEditMode } from '@modules/_shared/hooks/useBulkEditMode';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { LeadDetailsDrawer } from '@modules/lead-details/LeadDetailsDrawer';
import { LeadListSearch } from '@modules/leads/components/LeadListSearch';
import DialogAssignBroker from '@modules/leads/DialogAssignBroker';
import DialogUpdatePipeline from '@modules/leads/DialogUpdatePipeline';
import { useUpdateLeadsStageInCache } from '@modules/leads/kanbanCache';
import LeadsKanbanView from '@modules/leads/LeadsKanbanView';
import LeadsListView from '@modules/leads/LeadsListView';
import { GET_LEADS, GET_LEADS_COUNT } from '@modules/leads/leadsQueries';
import { useLeadsQuickFilters } from '@modules/leads/useLeadsQuickFilters';
import { useLocale } from '@shared/hooks/locale';

export type Lead = NonNullable<GetLeadsQuery['leads'][number]>;

export const initialLeadsListQuery = (
  searchParams: URLSearchParams,
): QueryOptions<GetLeadsQueryVariables> => {
  const where: Leads_Bool_Exp = prepareWhereClauseQuery(
    JSON.parse(searchParams.get('where') || '{}'),
  );

  const order_by: Leads_Order_By[] = JSON.parse(
    searchParams.get('order_by') || '[{ "requalified_or_created_at": "desc" }]',
  );

  return {
    query: GET_LEADS,
    variables: {
      where,
      offset: 0,
      limit: 25,
      order_by,
      hasUserFilter: false,
    },
  };
};

export type LeadsRowSelectionModel = {
  id: GridRowId;
  availableOperations: ('UPDATE_PIPELINE' | 'ASSIGN_BROKER')[];
}[];

export const Leads = () => {
  const apolloClient = useApolloClient();
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedRows, setSelectedRows } =
    useBulkEditMode<LeadsRowSelectionModel>([]);
  const navigate = useNavigate();
  const { me } = useAppData();
  const viewType = searchParams.get('view') ?? 'list';

  const updateLeadsStage = useUpdateLeadsStageInCache();

  const [assignBrokerDialog, setAssignBrokerDialog] = useState(false);
  const [pipelineDialog, setPipelineDialog] = useState(false);

  const selectedRowsIds = useMemo(
    () => selectedRows.map(row => row.id),
    [selectedRows],
  );

  const { variables } = useMemo(
    () => initialLeadsListQuery(searchParams),
    [searchParams],
  );
  const { where, order_by } = variables ?? {};

  const handleRefreshLeads = () => {
    apolloClient.refetchQueries({
      include: [GET_LEADS, GET_LEADS_COUNT],
    });
  };

  const pipelineId =
    searchParams.get('pipelineId_eq') ?? me?.tenant.default_pipeline_id ?? null;

  const canUpdatePipeline = useMemo(() => {
    if (selectedRows.length === 0) {
      return false;
    }

    return !selectedRows.some(
      row => !row.availableOperations.includes('UPDATE_PIPELINE'),
    );
  }, [selectedRows]);

  const quickFilters = useLeadsQuickFilters({ pipelineId });

  return (
    <>
      <SmartListTitle tableName="leads" defaultTitle={t('Leads')} />
      <ListToolbar
        tableName="leads"
        newLink={{
          pathname: 'new',
          search: searchParams.toString(),
        }}
        viewLinks={[
          { name: 'list', icon: LayoutList },
          { name: 'kanban', icon: Columns3 },
        ].map(({ name, icon: Icon }) => ({
          name,
          icon: <Icon size={18} strokeWidth={viewType === name ? 2.5 : 1.5} />,
        }))}
        refetch={handleRefreshLeads}
        searchComponent={viewType === 'kanban' ? undefined : <LeadListSearch />}
        quickFilters={quickFilters}
        bulkActions={
          <>
            {viewType !== 'kanban' && (
              <Button
                variant="action"
                disableElevation
                disabled={selectedRows.length === 0}
                onClick={() => {
                  setAssignBrokerDialog(true);
                }}
              >
                {t('Assign to broker')}
              </Button>
            )}
            <OptionalTooltip
              placement="top"
              title={t(
                'You can only update the stage of a lead if it has at least one agent assigned. Please assign an agent to the lead first.',
              )}
              disabled={canUpdatePipeline || selectedRows.length === 0}
            >
              <div>
                <Button
                  variant="action"
                  disableElevation
                  disabled={!canUpdatePipeline}
                  onClick={() => {
                    setPipelineDialog(true);
                  }}
                >
                  {t('Change stage')}
                </Button>
              </div>
            </OptionalTooltip>
            {viewType !== 'kanban' && (
              <DialogAssignBroker
                leadsIds={selectedRowsIds as string[]}
                open={assignBrokerDialog}
                onCancel={() => setAssignBrokerDialog(false)}
              />
            )}
            <DialogUpdatePipeline
              leadsIds={selectedRowsIds as string[]}
              pipeline="sales"
              open={pipelineDialog}
              initialPipelineId={pipelineId}
              onCancel={() => setPipelineDialog(false)}
              updateCacheFn={({ stageId }) => {
                // We only want to update the cache if we are in the kanban view
                if (viewType !== 'kanban') {
                  return;
                }

                // In this case, the selected rows are lead_agents, not leads.
                updateLeadsStage({
                  stageId,
                  leadAgentIds: selectedRowsIds as string[],
                  where,
                });
              }}
            />
          </>
        }
        showRealCountOption
      />

      {viewType === 'list' ? (
        <LeadsListView
          selectedRows={selectedRowsIds}
          setSelectedRows={setSelectedRows}
          where={where}
          order_by={order_by}
        />
      ) : (
        <LeadsKanbanView
          pipelineId={pipelineId}
          where={where}
          selectedRows={selectedRowsIds}
          setSelectedRows={setSelectedRows}
        />
      )}
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="new/*"
            element={
              <CreateLeadDrawer
                onClose={() => {
                  searchParams.delete('stage_id');
                  setSearchParams(searchParams);

                  navigate({
                    pathname: '.',
                    search: searchParams.toString(),
                  });
                }}
                onCreate={leadId => {
                  navigate({
                    pathname: `./${leadId}`,
                    search: searchParams.toString(),
                  });
                }}
              />
            }
          />

          <Route path=":leadId/*" element={<LeadDetailsDrawer />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default Leads;
